import React, {FC, memo, useState, useEffect, useRef} from "react";
import autoTable, {ColumnInput, RowInput} from "jspdf-autotable";
import {jsPDF as JsPDF} from "jspdf";
import moment from "moment";
import css from "./PdfListExport.less";
import {colorDarkGrey, colorGrey} from "../../styles/colors";
import i18n from "../../services/i18n";
import {ExportInputType} from "../ExportModal/ExportModal";

type PdfListExportProps = {
    message?: string;
    csvData: Array<{[key: string]: string}>;
    previewWidth?: number;
    fileName?: string;
    inputType: ExportInputType;
    action: "preview" | "export";
};

const PdfListExport: FC<PdfListExportProps> = ({
    message = "Hi",
    csvData,
    previewWidth = 1100,
    fileName = "Dateiname",
    action,
}) => {
    // console.log("csvData: ", csvData);
    const [tableData, setTableData] = useState<{
        columns: Array<ColumnInput>;
        body: Array<RowInput>;
    }>({columns: [], body: []});

    const [useCase, setUseCase] = useState<"preview" | "export">("preview");
    const [loading, setLoading] = useState(false);
    const blobUrlRef = useRef<string | null>(null);
    const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);

    useEffect(() => {
        setUseCase(action);
    }, [action]);

    useEffect(() => {
        const columns: Array<ColumnInput> = [];
        const body: Array<RowInput> = [];

        Object.keys(csvData[0]).forEach((key) => {
            const column: ColumnInput = {
                dataKey: key,
                header: key,
            };

            columns.push(column);
        });

        csvData.forEach((row) => {
            const bodyRow: Array<string> = [];

            Object.values(row).forEach((value) => {
                bodyRow.push(value);
            });

            body.push(bodyRow);
        });

        setTableData({columns, body});
    }, [csvData]);

    // Erzeuge Blob URL aus generateDoc() und räume alte URLs auf
    useEffect(() => {
        if (useCase !== "preview") return;

        // Alte Blob URL freigeben
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }

        try {
            const result = generateDoc(useCase);

            if (result instanceof Blob) {
                const url = URL.createObjectURL(result);
                blobUrlRef.current = url;
                setPreviewBlobUrl(url);
                setPreviewError(null);
            }
        } catch (e) {
            setPreviewError("PDF-Vorschau konnte nicht erstellt werden.");
            setPreviewBlobUrl(null);
        }

        return () => {
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [useCase, tableData, fileName]);

    // Export-Case: triggerbasiert
    useEffect(() => {
        if (useCase === "export") {
            generateDoc("export");
        }
    }, [useCase]);

    const generateDoc = (useCase: "export" | "preview") => {
        const pageWidth = 310;
        const pagePadding = 14;
        const innerPageWidth = pageWidth - 2 * pagePadding;

        const doc = new JsPDF({
            orientation: "landscape",
            // orientation: "portrait",
        });

        doc.setLineHeightFactor(1);

        doc.setFontSize(16);
        doc.setTextColor(colorDarkGrey);
        doc.text(fileName, pagePadding + 2, 15);
        doc.setDrawColor(colorGrey);
        // doc.line(pagePadding, 18, innerPageWidth, 18);

        doc.setTextColor(colorDarkGrey);

        doc.setLineHeightFactor(1.3);

        autoTable(doc, {
            startY: 18,
            theme: "grid",
            styles: {
                overflow: "ellipsize",
                fontSize: 9,
                halign: "left",
                cellPadding: {vertical: 2, horizontal: 2},
            },
            rowPageBreak: "auto",
            headStyles: {overflow: "linebreak", fillColor: "#7AB0FF"},
            bodyStyles: {valign: "top", overflow: "linebreak"},
            columnStyles: {
                bookingNumber: {
                    // font: "Helvetica",
                    fontStyle: "normal",
                    fontSize: 10,
                },
            }, // European countries centered

            columns: tableData.columns,
            body: tableData.body,
        });

        autoTable(doc, {
            body: [
                [
                    {
                        content:
                            i18n.containers.courses.AttendanceList.asOf() +
                            ": " +
                            moment().format("ddd - DD.MM.YYYY | HH:mm") +
                            " Uhr",
                        colSpan: 2,
                        rowSpan: 0,
                        styles: {
                            halign: "right",
                            fontSize: 7,
                        },
                    },
                ],
            ],
        });

        let previewPdf: string | Blob = "";

        const savePdf = async () => {
            return doc.save(
                fileName + "-" + moment().format("YYYY-MM-DD") + ".pdf",
            );
        };

        switch (useCase) {
            case "export":
                try {
                    setLoading(true);
                    savePdf().then(() => setLoading(false));
                } catch (error) {
                    throw new Error(`something went wrong here, ${error}`);
                } finally {
                    if (loading === false) {
                        setUseCase("preview");
                    }
                }
                break;
            case "preview":
                previewPdf = doc.output("blob");
                break;

            default:
                previewPdf = doc.output("blob");
                break;
        }

        return previewPdf;
    };

    const previewSize = (width: number) => {
        const factor = 297 / 210;

        return {
            width,
            height: width / factor,
            modalWidth: width + 50,
        };
    };

    return (
        <div id="PdfPreview">
            {previewError ? (
                <div style={{textAlign: "center", padding: 40}}>
                    {previewError}
                </div>
            ) : !previewBlobUrl ? (
                <div style={{textAlign: "center", padding: 40}}>
                    Vorschau wird geladen...
                </div>
            ) : (
                <iframe
                    width={previewSize(previewWidth).width}
                    height={previewSize(previewWidth).height}
                    src={`${previewBlobUrl}#toolbar=0&view=FitH&scrollbar=0`}
                    style={{border: "none"}}
                    title="PDF-Vorschau"
                />
            )}
        </div>
    );
};

export default memo(PdfListExport);
